import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  IEcommerceResultStream,
  IEcommerceStreamPaginationResponse
} from 'src/models/ecommerce';

interface IStream {
  streamPanelData: IEcommerceStreamPaginationResponse;
  ignoreStreamPanelData: IEcommerceStreamPaginationResponse;
  streamSelected: Array<IEcommerceResultStream>;
  isSelectedAllPrimary: boolean;
}

const initialState: IStream = {
  streamPanelData: {
    totalPages: 0,
    totalRows: 0,
    page: 0,
    limit: 0,
    result: []
  },
  ignoreStreamPanelData: {
    totalPages: 0,
    totalRows: 0,
    page: 0,
    limit: 0,
    result: []
  },
  streamSelected: [],
  isSelectedAllPrimary: false
};

const updateStreamPanelData = (
  prevTable: Array<IEcommerceResultStream>,
  payload: Array<IEcommerceResultStream>,
  updateProperties: (
    stream: IEcommerceResultStream
  ) => Partial<IEcommerceResultStream>
) => {
  const tempStream = [...prevTable];
  payload.forEach((stream: IEcommerceResultStream) => {
    const elementIndex = prevTable.findIndex(
      (element) =>
        `${element.originalId}${element.objectModel.id}` ===
        `${stream.originalId}${stream.objectModel.id}`
    );

    tempStream[elementIndex] = {
      ...tempStream[elementIndex],
      ...updateProperties(stream)
    };
  });
  return tempStream;
};

export const ecommerceStream = createSlice({
  name: 'ecommerceStreamPanel',
  initialState,
  reducers: {
    reducerUpdateStreamDataEcommerce: (
      state: IStream,
      action: PayloadAction<IEcommerceStreamPaginationResponse>
    ) => {
      state.streamPanelData = action.payload;
    },
    reducerUpdateStreamPanelByIgnored: (
      state: IStream,
      action: PayloadAction<IEcommerceStreamPaginationResponse>
    ) => {
      const updateTableStream = (
        prevTable: IEcommerceStreamPaginationResponse,
        streamsUpdated: IEcommerceResultStream[]
      ) => {
        return {
          ...prevTable,
          totalRows: prevTable.totalRows - streamsUpdated.length,
          result: prevTable.result.filter((currData) => {
            const isIgnored = streamsUpdated.find(
              (engaged) => engaged.originalId === currData.originalId
            );
            return !isIgnored;
          })
        };
      };
      state.streamPanelData = updateTableStream(
        state.streamPanelData,
        action.payload.result
      );
    },
    reducerUpdateIgnoreStreamDataEcommerce: (
      state: IStream,
      action: PayloadAction<IEcommerceStreamPaginationResponse>
    ) => {
      state.ignoreStreamPanelData = action.payload;
    },
    reducerUpdateIgnoreStreamPanelByIgnored: (
      state: IStream,
      action: PayloadAction<IEcommerceStreamPaginationResponse>
    ) => {
      const updateTableIgnoreStream = (
        ignoredStreamOutputWithPagination: IEcommerceStreamPaginationResponse,
        streamsUpdated: IEcommerceResultStream[]
      ) => {
        return {
          ...ignoredStreamOutputWithPagination,
          totalRows:
            ignoredStreamOutputWithPagination.totalRows + streamsUpdated.length,
          result: [
            ...ignoredStreamOutputWithPagination.result,
            ...streamsUpdated
          ]
        };
      };
      state.ignoreStreamPanelData = updateTableIgnoreStream(
        state.ignoreStreamPanelData,
        action.payload.result
      );
    },
    reducerUpdateStreamEcommerceSelected: (
      state: IStream,
      action: PayloadAction<Array<IEcommerceResultStream>>
    ) => {
      state.streamSelected = action.payload;
    },
    reducerUpdateIsSelectedAllPrimary: (
      state: IStream,
      action: PayloadAction<boolean>
    ) => {
      state.isSelectedAllPrimary = action.payload;
    },
    reducerUpdateDataStreamPanel: (
      state: IStream,
      action: PayloadAction<Array<IEcommerceResultStream>>
    ) => {
      state.streamPanelData.result = updateStreamPanelData(
        state.streamPanelData.result,
        action.payload,
        (stream) => ({
          sentiment: stream.sentiment,
          label: stream.label,
          isValidated: true
        })
      );
    },
    reducerUpdateLabelStreamPanel: (
      state: IStream,
      action: PayloadAction<Array<IEcommerceResultStream>>
    ) => {
      const updateTable = (
        prevTable: Array<IEcommerceResultStream>,
        payload: Array<IEcommerceResultStream>
      ) => {
        const tempStream = [...prevTable];
        payload.forEach((stream: IEcommerceResultStream) => {
          const elementIndex = prevTable.findIndex(
            (element) =>
              `${element.originalId}${element.objectModel.id}` ===
              `${stream.originalId}${stream.objectModel.id}`
          );

          tempStream[elementIndex] = {
            ...tempStream[elementIndex],
            label: stream.label
          };
        });
        return tempStream;
      };
      state.streamPanelData.result = updateTable(
        state.streamPanelData.result,
        action.payload
      );
    },
    reducerUpdatePartOfStreamPanel: (
      state: IStream,
      action: PayloadAction<Array<IEcommerceResultStream>>
    ) => {
      const updateTable = (prevTable, streamsUpdated) => {
        const tempStream = [...prevTable];
        streamsUpdated.forEach((stream) => {
          const elementIndex = prevTable.findIndex(
            (element) =>
              `${element.originalId}${element.objectModel.id}` ===
              `${stream.originalId}${stream.objectModel.id}`
          );

          tempStream[elementIndex] = {
            ...tempStream[elementIndex],
            sentiment: stream.sentiment,
            label: stream.label,
            isValidated: true
          };
        });
        return tempStream;
      };
      state.streamPanelData.result = updateTable(
        state.streamPanelData.result,
        action.payload
      );
    }
  }
});

export const {
  reducerUpdateStreamDataEcommerce,
  reducerUpdateStreamPanelByIgnored,
  reducerUpdateIgnoreStreamDataEcommerce,
  reducerUpdateIgnoreStreamPanelByIgnored,
  reducerUpdateStreamEcommerceSelected,
  reducerUpdateIsSelectedAllPrimary,
  reducerUpdateDataStreamPanel,
  reducerUpdateLabelStreamPanel,
  reducerUpdatePartOfStreamPanel
} = ecommerceStream.actions;

export default ecommerceStream.reducer;
